import React from 'react';
import CodeSnippet from './CodeSnippet';
import {
  DocContainer,
  Title,
  SubTitle,
  Paragraph,
  List,
  ListItem,
  OrderedList,
  OrderedListItem,
  Code,
} from './DocStyles';

const RotasDoc: React.FC = () => {
  return (
    <DocContainer>
      <Title>Sistema de Navegação (Rotas) no SmartEntrega</Title>

      <Paragraph>
        Este documento explica como funciona o sistema de rotas no projeto
        SmartEntrega, utilizando o React Router para gerenciar a navegação entre
        diferentes páginas da aplicação.
      </Paragraph>

      <SubTitle>Visão Geral do React Router</SubTitle>
      <Paragraph>
        O React Router é uma biblioteca que permite implementar navegação e
        roteamento em aplicações React. No SmartEntrega, utilizamos o React
        Router v6, que oferece uma API declarativa para definir rotas.
      </Paragraph>

      <SubTitle>Estrutura de Rotas no SmartEntrega</SubTitle>
      <Paragraph>
        As rotas da aplicação são definidas no arquivo{' '}
        <Code>src/routes/index.tsx</Code>. Este arquivo configura todas as rotas
        disponíveis, incluindo:
      </Paragraph>
      <List>
        <ListItem>Rotas públicas (acessíveis sem autenticação)</ListItem>
        <ListItem>Rotas protegidas (requerem autenticação)</ListItem>
        <ListItem>
          Rotas administrativas (requerem permissões específicas)
        </ListItem>
      </List>

      <SubTitle>Arquivo de Rotas Principal</SubTitle>
      <CodeSnippet
        code={`// src/routes/index.tsx
import React, { useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppSelector } from '../store';

import Login from '../pages/Login';
import Dashboard from '../pages/Dashboard';
import Cadastros from '../pages/Cadastros';
import Relatorios from '../pages/Relatorios';
import Documentacao from '../pages/Documentacao';
import NotFound from '../pages/NotFound';
import Layout from '../components/Layout';
import ProtectedAdminRoute from '../components/ProtectedAdminRoute';

// Componente para rotas protegidas (requer autenticação)
const ProtectedRoute = ({ children }: { children: React.ReactElement }) => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Componente de layout para páginas autenticadas
const AppLayout = () => {
  const location = useLocation();
  
  // Determina o título da página com base na rota atual
  const pageTitle = useMemo(() => {
    const path = location.pathname;
    
    switch (path) {
      case '/':
        return 'Home';
      case '/cadastros':
        return 'Cadastro de Usuários';
      case '/relatorios':
        return 'Relatórios';
      case '/documentacao':
        return 'Documentação';
      default:
        return 'SmartEntrega';
    }
  }, [location.pathname]);
  
  return (
    <Layout pageTitle={pageTitle}>
      <Outlet />
    </Layout>
  );
};

// Componente principal de rotas
const AppRoutes = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Rotas públicas */}
        <Route path="/login" element={<Login />} />
        
        {/* Rotas protegidas com Layout */}
        <Route 
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Dashboard />} />
          <Route path="/cadastros" element={<Cadastros />} />
          <Route path="/relatorios" element={<Relatorios />} />
          
          {/* Rota de documentação - acessível apenas para usuários com codigoGrupoUsuario = 1 */}
          <Route 
            path="/documentacao" 
            element={
              <ProtectedAdminRoute>
                <Documentacao />
              </ProtectedAdminRoute>
            } 
          />
        </Route>
        
        {/* Rota para página não encontrada */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRoutes;`}
      />

      <SubTitle>Tipos de Rotas</SubTitle>

      <SubTitle>1. Rotas Públicas</SubTitle>
      <Paragraph>
        Rotas que não requerem autenticação, como a página de login:
      </Paragraph>
      <CodeSnippet code={`<Route path="/login" element={<Login />} />`} />

      <SubTitle>2. Rotas Protegidas</SubTitle>
      <Paragraph>
        Rotas que requerem que o usuário esteja autenticado:
      </Paragraph>
      <CodeSnippet
        code={`<Route 
  element={
    <ProtectedRoute>
      <AppLayout />
    </ProtectedRoute>
  }
>
  <Route path="/" element={<Dashboard />} />
  <Route path="/cadastros" element={<Cadastros />} />
  <Route path="/relatorios" element={<Relatorios />} />
</Route>`}
      />

      <SubTitle>3. Rotas Administrativas</SubTitle>
      <Paragraph>
        Rotas que requerem permissões administrativas específicas:
      </Paragraph>
      <CodeSnippet
        code={`<Route 
  path="/documentacao" 
  element={
    <ProtectedAdminRoute>
      <Documentacao />
    </ProtectedAdminRoute>
  } 
/>`}
      />

      <SubTitle>Componentes de Proteção de Rotas</SubTitle>

      <SubTitle>ProtectedRoute</SubTitle>
      <Paragraph>
        Este componente verifica se o usuário está autenticado antes de permitir
        o acesso à rota:
      </Paragraph>
      <CodeSnippet
        code={`// src/components/ProtectedRoute.tsx
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAppSelector } from '../store';

interface ProtectedRouteProps {
  children: React.ReactElement;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  
  if (!isAuthenticated) {
    // Redireciona para a página de login se não estiver autenticado
    return <Navigate to="/login" replace />;
  }
  
  // Renderiza o conteúdo protegido se estiver autenticado
  return children;
};

export default ProtectedRoute;`}
      />

      <SubTitle>ProtectedAdminRoute</SubTitle>
      <Paragraph>
        Este componente verifica se o usuário tem permissões administrativas:
      </Paragraph>
      <CodeSnippet
        code={`// src/components/ProtectedAdminRoute.tsx
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAppSelector } from '../store';

interface ProtectedAdminRouteProps {
  children: React.ReactElement;
}

const ProtectedAdminRoute: React.FC<ProtectedAdminRouteProps> = ({ children }) => {
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  
  // Verifica se o usuário está autenticado e tem o código de grupo correto
  if (!isAuthenticated || user?.codigoGrupoUsuario !== 1) {
    // Redireciona para a página inicial se não tiver permissão
    return <Navigate to="/" replace />;
  }
  
  // Renderiza o conteúdo protegido se tiver permissão
  return children;
};

export default ProtectedAdminRoute;`}
      />

      <SubTitle>Layout com Outlet</SubTitle>
      <Paragraph>
        O React Router v6 introduziu o componente Outlet, que funciona como um
        espaço reservado onde os componentes filhos de uma rota serão
        renderizados:
      </Paragraph>
      <CodeSnippet
        code={`const AppLayout = () => {
  const location = useLocation();
  
  const pageTitle = useMemo(() => {
    // Lógica para determinar o título da página
  }, [location.pathname]);
  
  return (
    <Layout pageTitle={pageTitle}>
      <Outlet /> {/* Aqui serão renderizados os componentes das rotas filhas */}
    </Layout>
  );
};`}
      />

      <SubTitle>Navegação Programática</SubTitle>
      <Paragraph>
        Para navegar entre páginas programaticamente (por exemplo, após um login
        bem-sucedido), use o hook useNavigate:
      </Paragraph>
      <CodeSnippet
        code={`import { useNavigate } from 'react-router-dom';

const LoginForm = () => {
  const navigate = useNavigate();
  
  const handleLoginSuccess = () => {
    // Redireciona para a página inicial após o login
    navigate('/');
  };
  
  // ...
};`}
      />

      <SubTitle>Links de Navegação</SubTitle>
      <Paragraph>
        Para criar links de navegação em sua aplicação, use o componente Link ou
        NavLink:
      </Paragraph>
      <CodeSnippet
        code={`import { Link, NavLink } from 'react-router-dom';

const Navigation = () => {
  return (
    <nav>
      <ul>
        <li>
          <Link to="/">Dashboard</Link>
        </li>
        <li>
          {/* NavLink adiciona a classe 'active' quando a rota está ativa */}
          <NavLink 
            to="/cadastros"
            className={({ isActive }) => isActive ? 'active-link' : ''}
          >
            Cadastros
          </NavLink>
        </li>
        <li>
          <NavLink to="/relatorios">Relatórios</NavLink>
        </li>
      </ul>
    </nav>
  );
};`}
      />

      <SubTitle>Parâmetros de Rota</SubTitle>
      <Paragraph>
        Para criar rotas com parâmetros dinâmicos, use a sintaxe :parametro:
      </Paragraph>
      <CodeSnippet
        code={`// Definição da rota
<Route path="/usuario/:id" element={<PerfilUsuario />} />

// Componente que acessa o parâmetro
import { useParams } from 'react-router-dom';

const PerfilUsuario = () => {
  const { id } = useParams();
  
  // Agora você pode usar o ID para buscar dados do usuário
  
  return <div>Perfil do Usuário {id}</div>;
};`}
      />

      <SubTitle>Rotas Aninhadas</SubTitle>
      <Paragraph>
        O React Router v6 facilita a criação de rotas aninhadas:
      </Paragraph>
      <CodeSnippet
        code={`<Route path="/configuracoes" element={<Configuracoes />}>
  <Route index element={<ConfigGeral />} /> {/* Rota padrão */}
  <Route path="perfil" element={<ConfigPerfil />} />
  <Route path="notificacoes" element={<ConfigNotificacoes />} />
</Route>`}
      />

      <SubTitle>Como Adicionar uma Nova Rota</SubTitle>
      <Paragraph>Para adicionar uma nova rota ao SmartEntrega:</Paragraph>
      <OrderedList>
        <OrderedListItem>
          Crie o componente da página em <Code>src/pages/</Code>
        </OrderedListItem>
        <OrderedListItem>
          Adicione a rota no arquivo <Code>src/routes/index.tsx</Code>
        </OrderedListItem>
        <OrderedListItem>
          Determine o tipo de proteção necessária (pública, protegida,
          administrativa)
        </OrderedListItem>
        <OrderedListItem>
          Adicione um link de navegação para a nova página (se necessário)
        </OrderedListItem>
      </OrderedList>

      <SubTitle>Exemplo: Adicionando uma Rota de Configurações</SubTitle>
      <CodeSnippet
        code={`// 1. Crie o componente da página
// src/pages/Configuracoes.tsx
import React from 'react';

const Configuracoes: React.FC = () => {
  return (
    <div>
      <h1>Configurações do Sistema</h1>
      {/* Conteúdo da página */}
    </div>
  );
};

export default Configuracoes;

// 2. Adicione a rota no arquivo de rotas
// src/routes/index.tsx
// ...
import Configuracoes from '../pages/Configuracoes';

const AppRoutes = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Rotas existentes */}
        
        {/* Nova rota protegida */}
        <Route 
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          {/* Rotas existentes */}
          <Route path="/" element={<Dashboard />} />
          <Route path="/cadastros" element={<Cadastros />} />
          <Route path="/relatorios" element={<Relatorios />} />
          
          {/* Nova rota */}
          <Route path="/configuracoes" element={<Configuracoes />} />
          
          {/* Outras rotas */}
        </Route>
      </Routes>
    </BrowserRouter>
  );
};`}
      />

      <SubTitle>Conclusão</SubTitle>
      <Paragraph>
        O sistema de rotas no SmartEntrega utiliza o React Router v6 para
        gerenciar a navegação entre diferentes páginas da aplicação. A estrutura
        de rotas inclui proteção para rotas que requerem autenticação ou
        permissões específicas, garantindo que apenas usuários autorizados
        possam acessar determinadas funcionalidades.
      </Paragraph>
    </DocContainer>
  );
};

export default RotasDoc;
